import React, { useState, useEffect, useRef, useCallback } from "react";
import { GoogleMap } from "@react-google-maps/api";
import parseGeoraster from "georaster";
import { useSelectedRecommendedProjectSelector } from "../../../store/projects/selectors";
import { mapStyles } from "../PlanetTab/GoogleMapStyle";
import ReactDOMServer from "react-dom/server";
import { BsFillLayersFill } from "react-icons/bs";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { setCurrentDataLayerType, setCurrentGeoRasterData } from "../../../store/projects/actions";
import { dispatch } from "../../../store";
import { useCurrentDataLayerTypeSelector } from "../../../store/projects/selectors";

const PeopleTabMap = () => {
  const currentDataLayerType = useCurrentDataLayerTypeSelector();
  const [center, setCenter] = useState<{ lat: number; lng: number }>({ lat: 9.43, lng: 80.18 });
  const [zoom, setZoom] = useState<number>(8);
  const [selectedLayer, setSelectedLayer] = useState<string>("ghs");
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [displayLegend, setDisplayLegend] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const selectedReProject = useSelectedRecommendedProjectSelector();

  // use Refs
  const mapRef = useRef<any>(null);
  const dataOverLayRef = useRef<google.maps.GroundOverlay | null>(null);
  const legendRef = useRef<HTMLDivElement | null>(null);
  const loadingOverlayRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch(setCurrentDataLayerType(selectedLayer));
  }, [selectedLayer]);

  useEffect(() => {
    setSelectedLayer(currentDataLayerType);
  }, [currentDataLayerType]);

  useEffect(() => {
    if (selectedReProject?.adm2) {
      loadRasterImage(selectedLayer);
    }
  }, [selectedReProject?.adm2, selectedLayer]);

  const loadRasterImage = async (layerType: string) => {
    try {
      // Check if Google Maps API is loaded
      if (typeof google === "undefined" || !google.maps) {
        console.error("Google Maps API not loaded yet");
        return;
      }

      // setGroundOverlay(null);
      setIsLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/v2/project-data-raster/?adm2=${selectedReProject?.adm2}&type=${layerType}`,
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch raster data: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const geoRaster = await parseGeoraster(arrayBuffer);
      // setGeoRasterData(geoRaster);

      let canvas = canvasRef.current;
      if (!canvas) {
        canvas = document.createElement("canvas");
        canvasRef.current = canvas;
      }

      canvas.width = geoRaster.width;
      canvas.height = geoRaster.height;
      const ctx = canvas.getContext("2d");

      if (!ctx) throw new Error("Failed to get canvas context.");

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const imageData = ctx.createImageData(geoRaster.width, geoRaster.height);
      const values = geoRaster.values[0];

      for (let y = 0; y < geoRaster.height; y++) {
        for (let x = 0; x < geoRaster.width; x++) {
          const pixelValue = values[y][x];
          const index = (y * geoRaster.width + x) * 4;

          if (pixelValue > 0) {
            const color = getColorForValue(pixelValue); // Get color for the current pixel value
            imageData.data[index] = color.r;
            imageData.data[index + 1] = color.g;
            imageData.data[index + 2] = color.b;
            imageData.data[index + 3] = 255; // Fully opaque
          } else {
            imageData.data[index] = 0;
            imageData.data[index + 1] = 0;
            imageData.data[index + 2] = 0;
            imageData.data[index + 3] = 0; // Fully transparent for no data
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);
      const base64Image = canvas.toDataURL("image/png");

      const bounds = {
        north: geoRaster.ymax,
        south: geoRaster.ymin,
        east: geoRaster.xmax,
        west: geoRaster.xmin,
      };

      // remove existing layer
      if (dataOverLayRef.current) {
        try {
          if (dataOverLayRef.current.setMap) {
            dataOverLayRef.current.setMap(null);
          }
        } catch (error) {
          console.error("Error removing existing overlay:", error);
        }
      }

      // Check if map and Google Maps API are available
      if (!mapRef.current || typeof google === "undefined" || !google.maps) {
        console.error("Map or Google Maps API not available");
        return;
      }

      // create a new data overlay
      const newDataOverlay = new google.maps.GroundOverlay(base64Image, bounds);
      if (mapRef.current && newDataOverlay.setMap) {
        newDataOverlay.setMap(mapRef.current);
        dataOverLayRef.current = newDataOverlay;
      }

      const latCenter = (geoRaster.ymax + geoRaster.ymin) / 2;
      const lngCenter = (geoRaster.xmax + geoRaster.xmin) / 2;
      setCenter({ lat: latCenter, lng: lngCenter });

      const latDiff = Math.abs(geoRaster.ymax - geoRaster.ymin);
      const lngDiff = Math.abs(geoRaster.xmax - geoRaster.xmin);
      const maxDiff = Math.max(latDiff, lngDiff);

      const calculatedZoom = getZoomLevel(maxDiff);
      setZoom(calculatedZoom);
      const geoRasterDataObject = {
        data: geoRaster,
        isLoading: isLoading,
      };
      dispatch(setCurrentGeoRasterData(geoRasterDataObject));
      return geoRaster;
    } catch (error) {
      console.error("An error occurred while getting the raster file:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Define a function to get the color based on the raster value
  const getColorForValue = (value: number) => {
    const landUseColors: { [key: number]: { r: number; g: number; b: number } } = {
      1: { r: 0, g: 0, b: 255 }, // Blue
      2: { r: 0, g: 128, b: 0 }, // Green
      4: { r: 255, g: 165, b: 0 }, // Orange
      5: { r: 255, g: 0, b: 0 }, // Red
      7: { r: 128, g: 0, b: 128 }, // Purple
      8: { r: 165, g: 42, b: 42 }, // Brown
      9: { r: 0, g: 255, b: 255 }, // Cyan
      10: { r: 128, g: 128, b: 128 }, // Gray
      11: { r: 128, g: 128, b: 0 }, // Olive
    };

    return landUseColors[value] || { r: 0, g: 0, b: 0 }; // Default to black if value not found
  };

  const getZoomLevel = (boundDiff: number) => {
    if (boundDiff < 0.1) return 12;
    if (boundDiff < 0.5) return 10;
    if (boundDiff < 1) return 8;
    if (boundDiff < 2) return 6;
    return 8;
  };

  const layers = [{ value: "ghs", label: "GHS" }];

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;

      if (!loadingOverlayRef.current) {
        const loadingOverlay = document.createElement("div");
        loadingOverlay.style.position = "absolute";
        loadingOverlay.style.top = "0";
        loadingOverlay.style.left = "0";
        loadingOverlay.style.width = "100%";
        loadingOverlay.style.height = "100%";
        loadingOverlay.style.justifyContent = "center";
        loadingOverlay.style.alignItems = "center";
        loadingOverlay.style.backgroundColor = "rgba(255, 255, 255, 0.6)";
        loadingOverlay.style.zIndex = "1000";
        loadingOverlay.style.display = "none"; // Initially hidden

        const spinnerIcon = document.createElement("div");
        spinnerIcon.innerHTML = ReactDOMServer.renderToString(<LoadingSpinner />);
        loadingOverlay.appendChild(spinnerIcon);

        loadingOverlayRef.current = loadingOverlay;
        map.getDiv().appendChild(loadingOverlay); // Attach to map container
      }

      // Create layer control button
      const controlDiv = document.createElement("div");
      controlDiv.style.position = "relative";

      // Create the button for the control
      const controlButton = document.createElement("button");
      controlButton.style.marginTop = "10px";
      controlButton.style.marginRight = "10px";
      controlButton.style.background = "#fff";
      controlButton.style.borderRadius = "2px";
      controlButton.style.padding = "10px";
      controlButton.style.cursor = "pointer";
      controlButton.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";
      controlButton.style.fontSize = "20px";
      controlButton.innerHTML = ReactDOMServer.renderToString(<BsFillLayersFill />);

      controlButton.addEventListener("click", () => {
        setDisplayLegend((prev) => !prev); // Toggle the legend visibility
      });

      controlDiv.appendChild(controlButton);
      map.controls[google.maps.ControlPosition.TOP_LEFT].push(controlDiv);

      // Create the legend menu
      const legendParentDiv = document.createElement("div");
      legendParentDiv.style.position = "absolute";
      legendParentDiv.style.top = "50px";
      legendParentDiv.style.left = "0px";
      legendParentDiv.style.width = "max-content";
      legendParentDiv.style.padding = "10px";
      legendParentDiv.style.backgroundColor = "white";
      legendParentDiv.style.boxShadow = "0 2px 6px rgba(0,0,0,0.3)";
      legendParentDiv.style.display = "none"; // Initially hidden

      // loop through layers to map each layer to a selection option
      layers.map((layer) => {
        // Add content to the legend
        const legendmenuItem = document.createElement("div");
        legendmenuItem.style.marginBottom = "8px";
        legendmenuItem.style.display = "flex";
        legendmenuItem.style.alignItems = "center";
        legendmenuItem.style.cursor = "pointer";
        legendmenuItem.addEventListener("click", () => {
          setSelectedLayer(layer.value);
        });

        // check box div
        const checkBoxDiv = document.createElement("div");
        checkBoxDiv.id = layer.value;
        checkBoxDiv.style.marginRight = "8px";
        checkBoxDiv.style.height = "16px";
        checkBoxDiv.style.width = "16px";
        checkBoxDiv.style.backgroundColor = "#e3f2fd";
        checkBoxDiv.style.borderRadius = "50%";
        checkBoxDiv.style.border = selectedLayer === checkBoxDiv.id ? "2px solid black" : "none";

        // name goes here
        const legendLabel = document.createElement("label");
        legendLabel.innerText = layer.label;
        legendLabel.style.cursor = "pointer";
        legendLabel.style.fontSize = "16px";

        legendmenuItem.appendChild(checkBoxDiv);
        legendmenuItem.appendChild(legendLabel);
        legendParentDiv.appendChild(legendmenuItem);
      });

      // Save the legend reference
      legendRef.current = legendParentDiv;
      controlDiv.appendChild(legendParentDiv);
    },
    [selectedLayer],
  );

  useEffect(() => {
    if (loadingOverlayRef.current) {
      loadingOverlayRef.current.style.display = isLoading ? "flex" : "none";
    }
  }, [isLoading]);

  useEffect(() => {
    if (legendRef.current) {
      legendRef.current.style.display = displayLegend ? "block" : "none";
    }
  }, [displayLegend]);

  useEffect(() => {
    if (legendRef.current) {
      const checkBoxDivs = legendRef.current.querySelectorAll("div[id]");
      checkBoxDivs.forEach((div: any) => {
        div.style.border = selectedLayer === div.id ? "2px solid black" : "1px solid #e0e0e0";
      });
    }
  }, [selectedLayer]);

  return (
    <>
      <GoogleMap
        mapContainerStyle={{
          width: "100%",
          height: "540px",
          transform: "translate3d(0, 0, 1px)",
        }}
        zoom={zoom}
        center={center}
        options={{
          styles: mapStyles,
        }}
        onLoad={onMapLoad}
      />
    </>
  );
};

export { PeopleTabMap };
